const isNoBackendMode = process.env.REACT_APP_NO_BACKEND === '1';

let mockDataPromise = null;

function normalizePath(path) {
  const rawPath = String(path || '').trim();
  const cleanPath = rawPath.replace(/^https?:\/\/[^/]+/i, '').split('?')[0].trim();
  if (!cleanPath) {
    return '/';
  }

  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  return normalizedPath.length > 1 ? normalizedPath.replace(/\/+$/, '') : normalizedPath;
}

function clone(value) {
  if (value === null || value === undefined) {
    return value;
  }
  return JSON.parse(JSON.stringify(value));
}

function defaultDataForMethod(method) {
  const upperMethod = String(method || '').toUpperCase();
  if (upperMethod === 'POST' || upperMethod === 'DELETE') {
    return { success: true };
  }
  return {};
}

function buildResponse(payload, method) {
  if (payload && typeof payload === 'object' && 'status' in payload && ('data' in payload || 'body' in payload)) {
    return {
      status: payload.status,
      data: clone(payload.data),
      body: typeof payload.body === 'string' ? payload.body : null,
      headers: clone(payload.headers || {}),
    };
  }

  return {
    status: 200,
    data: clone(payload ?? defaultDataForMethod(method)),
    body: null,
    headers: {},
  };
}

function matchesPattern(pattern, normalizedPath) {
  const normalizedPattern = normalizePath(pattern);
  if (normalizedPattern === normalizedPath) {
    return true;
  }

  const regexPattern = normalizedPattern
    .replace(/:([^/]+)/g, '__PARAM__')
    .replace(/\*/g, '__WILDCARD__')
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/__PARAM__/g, '[^/]+')
    .replace(/__WILDCARD__/g, '.*');

  return new RegExp(`^${regexPattern}$`).test(normalizedPath);
}

function findMockPayload(mockData, method, normalizedPath) {
  const methodEntries = mockData?.[method] || {};
  if (normalizedPath in methodEntries) {
    return methodEntries[normalizedPath];
  }

  for (const [pattern, payload] of Object.entries(methodEntries)) {
    if (pattern === '*' || pattern === '_default') {
      continue;
    }

    if (matchesPattern(pattern, normalizedPath)) {
      return payload;
    }
  }

  return methodEntries['*'] ?? methodEntries._default;
}

function mergeMockData(base, patch) {
  const merged = { ...(base || {}) };
  for (const [method, entries] of Object.entries(patch || {})) {
    merged[method] = {
      ...(merged[method] || {}),
      ...(entries || {}),
    };
  }
  return merged;
}

async function fetchMockJson(path) {
  const response = await fetch(path, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Unable to load mock data: ${response.status} (${path})`);
  }
  return await response.json();
}

async function loadMockData() {
  if (!mockDataPromise) {
    mockDataPromise = (async () => {
      try {
        const manifest = await fetchMockJson('/data/mock/index.json');
        const files = Array.isArray(manifest?.files) ? manifest.files : [];
        if (!files.length) {
          return {};
        }

        let merged = {};
        for (const file of files) {
          const partial = await fetchMockJson(`/data/mock/${file}`);
          merged = mergeMockData(merged, partial);
        }
        return merged;
      } catch (multiFileError) {
        try {
          return await fetchMockJson('/data/mock/mock-api.json');
        } catch (legacyError) {
          console.warn('Falling back to empty no-backend mock data.', multiFileError, legacyError);
          return {};
        }
      }
    })().catch((error) => {
        console.warn('Falling back to empty no-backend mock data.', error);
        return {};
      });
  }
  return await mockDataPromise;
}

async function getMockResponse(method, path) {
  const upperMethod = String(method || 'GET').toUpperCase();
  const normalizedPath = normalizePath(path);
  const mockData = await loadMockData();
  const matched = findMockPayload(mockData, upperMethod, normalizedPath);
  return buildResponse(matched, upperMethod);
}

export async function mockAxiosRequest(method, path) {
  return await getMockResponse(method, path);
}

function createReadableStream(body) {
  if (typeof ReadableStream === 'undefined') {
    return undefined;
  }

  return new ReadableStream({
    start(controller) {
      controller.enqueue(new TextEncoder().encode(body));
      controller.close();
    },
  });
}

function createFetchResponse(status, data, body, headers = {}) {
  const responseBody = body ?? JSON.stringify(data ?? {});
  const responseHeaders = {
    'Content-Type': body === null ? 'application/json' : 'text/plain; charset=utf-8',
    ...headers,
  };

  if (typeof Response !== 'undefined') {
    return new Response(createReadableStream(responseBody) ?? responseBody, {
      status,
      headers: responseHeaders,
    });
  }

  return {
    ok: status >= 200 && status < 300,
    status,
    json: async () => clone(data),
    text: async () => responseBody,
    headers: responseHeaders,
    body: {
      getReader() {
        let isDone = false;
        return {
          async read() {
            if (isDone) {
              return { done: true, value: undefined };
            }

            isDone = true;
            return {
              done: false,
              value: new TextEncoder().encode(responseBody),
            };
          },
        };
      },
    },
  };
}

export async function mockFetchRequest(method, path) {
  const response = await getMockResponse(method, path);
  return createFetchResponse(response.status, response.data, response.body, response.headers);
}

export { isNoBackendMode };
